import { AppState } from './state.js';
import { SharedScene } from './scene.js';
import { StadiumLoader } from './loader.js';
import { UIController } from './ui.js';
import { MapsView } from './views/mapsView.js';
import { BasicView } from './views/basicView.js';
import { getStadiumMetadata } from './stadiumHelper.js';

// Setup orchestrator
document.addEventListener('DOMContentLoaded', () => {
  const sharedScene = new SharedScene();
  const loader = new StadiumLoader();
  const ui = new UIController(loader, sharedScene);

  let mapsView = null;
  if (!import.meta.env.PROD) {
    mapsView = new MapsView('map-container', sharedScene, AppState);
  }
  const basicView = new BasicView('basic-container', sharedScene, AppState);

  let previousMapsEnabled = null;

  // Subscribe to changes in global AppState
  AppState.subscribe(async (state) => {
    // 1. Update Sun directional light vectors
    const meta = getStadiumMetadata(state.stadiumName);
    const timeZone = meta ? meta.tz : 'America/Chicago';
    sharedScene.updateSun(state.lat, state.lng, state.date, state.time, timeZone);

    // 2. Apply Model Transforms (Scale & Rotation)
    const finalScale = state.baseScale * state.scale;
    sharedScene.stadiumGroup.scale.set(finalScale, finalScale, finalScale);
    
    // Z-Up Map Heading: Map compass expects clockwise (negative), Three.js expects CCW.
    sharedScene.stadiumGroup.rotation.z = -(state.rotation * Math.PI / 180);

    // 3. Handle View Mode Transitions (Google Maps vs. Fallback Basic View)
    if (!import.meta.env.PROD) {
      if (state.mapsEnabled !== previousMapsEnabled) {
        previousMapsEnabled = state.mapsEnabled;

        if (state.mapsEnabled) {
          // Toggle Google Maps ON
          basicView.hide();

          const loaderElement = document.getElementById('loading-overlay');
          loaderElement.style.display = 'flex';
          loaderElement.innerText = 'Initializing 3D Google Maps...';

          try {
            await mapsView.init();
            mapsView.show();
          } catch (err) {
            console.error('Failed to initialize Google Maps:', err);
            alert('Google Maps initialization failed. Falling back to Basic 3D Mode.');
            AppState.updateState({ mapsEnabled: false });
          } finally {
            loaderElement.style.display = 'none';
            loaderElement.innerText = 'Loading...';
          }
        } else {
          // Toggle Google Maps OFF
          if (mapsView) mapsView.hide();
          basicView.init();
          basicView.show();
        }
      } else {
        // Re-trigger viewport redraws or pan map if coordinates changed
        if (state.mapsEnabled && mapsView) {
          mapsView.updatePosition(state.lat, state.lng);
        }
      }
    } else {
      // In production, force basic view to initialize and show once on startup
      if (previousMapsEnabled === null) {
        previousMapsEnabled = false;
        basicView.init();
        basicView.show();
      }
    }
  });

  // Load the default model (Stadium_KC.glb) always on startup
  const loadStartupModel = async () => {
    const modelName = 'Stadium_KC.glb';

    const loaderElement = document.getElementById('loading-overlay');
    loaderElement.style.display = 'flex';
    loaderElement.innerText = `Loading Stadium ${modelName.replace('Stadium_', '').replace('.glb', '')}...`;

    try {
      const result = await loader.loadFromUrl(`/models/${modelName}`, sharedScene.stadiumGroup);
      AppState.updateState({
        baseScale: result.baseScale,
        stadiumName: result.stadiumName
      });
    } catch (err) {
      console.warn(`Failed to load startup model /models/${modelName}:`, err);
      // Fallback to Stadium_KC if the saved cache model failed to load
      if (modelName !== 'Stadium_KC.glb') {
        try {
          const fallbackResult = await loader.loadFromUrl('/models/Stadium_KC.glb', sharedScene.stadiumGroup);
          AppState.updateState({
            baseScale: fallbackResult.baseScale,
            stadiumName: fallbackResult.stadiumName
          });
        } catch (fbErr) {
          console.error('Absolute fallback loading failed:', fbErr);
        }
      }
    } finally {
      loaderElement.style.display = 'none';
      loaderElement.innerText = 'Loading...';
    }
  };

  // Trigger startup load
  loadStartupModel();

  // Open the drawer on startup programmatically
  if (ui && ui.bsOffcanvas) {
    ui.bsOffcanvas.show();
  }
});
